import React, {useState, useMemo, useRef} from 'react';
import {DndProvider, useDrag, useDrop} from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import styles from '../ProjectPhasesBar.module.scss';
import DeliverableCardContent from './DeliverableCardContent.jsx';
import RecurringDeliverableCardContent from './RecurringDeliverableCardContent.jsx';
import AddDeliverable from './AddDeliverable.jsx';

import {
  project_phase_update_deliverable_order_path,
} from '~/routes';

const DeliverablesBar = ({project_id, phaseId, phases, setPhases, onNextStep, canManageProjectPhases}) => {
  const shownPhase = phases.find((p) => p.id === phaseId);
  const isLastPhase = useMemo(() => shownPhase.idx === phases.count - 1, [shownPhase.idx]);
  const [isEditing, setIsEditing] = useState(false);

  const outgoingTransition = useMemo(() => {
    const t = shownPhase.outgoing?.[0];
    if (Object.keys(t?.rule_set || {}).length > 0) {
      const rule = shownPhase.outgoing_rules.find(t => t.id === t.id) || {};
      return {...t, ...rule}
    }
    return t;
  }, [shownPhase.outgoing])

  const outgoingRules = useMemo(
    () => (shownPhase.outgoing_rules || []).find(t => t.id === outgoingTransition?.id)?.rules,
    [outgoingTransition]
  )

  const outgoingBlocked = useMemo(
    () => !shownPhase.current || !(outgoingRules && outgoingRules.every(r => r.checked === true)),
    [shownPhase.current, outgoingTransition]
  );

  const handleNextStep = () => {
    if (outgoingTransition.link) {
      window.open(outgoingTransition.link, '_blank', 'noopener')
    } else {
      onNextStep(outgoingTransition.to_phase_id)
    }
  }

  const updateDeliverableProgress = (deliverable_id, newValue) => {
    setPhases((oldPhases) => {
      const phaseIdx = oldPhases.findIndex(p => p.id === phaseId);
      const dIdx = oldPhases[phaseIdx].deliverables.findIndex(d => d.id === deliverable_id);

      return update(oldPhases, {[phaseIdx]: {deliverables: {[dIdx]: {completion_percentage: {$set: newValue}}}}});
    });
  }

  const updateDeliverableSequences = (deliverable_id, newItem) => {
    setPhases(oldPhases => {
      const pIdx = oldPhases.findIndex(p => p.id === phaseId);
      const dIdx = oldPhases[pIdx].deliverables.findIndex(d => d.id === deliverable_id);
      const sequence_entries = oldPhases[pIdx].deliverables[dIdx].sequence_entries || [];
      const sIdx = sequence_entries.findIndex(i => i.id === newItem.id);

      return update(oldPhases, {
        [pIdx]: {deliverables: {[dIdx]: {sequence_entries: sIdx > -1 ? {[sIdx]: {$set: newItem}} : {$push: [newItem]}}}}
      })
    })
  }

  const moveDeliverable = (fromIndex, toIndex) => {
    const phaseIndex = phases.findIndex((p) => p.id === shownPhase.id);

    if (phaseIndex >= 0)
      setPhases((prev) =>
        update(prev, {
          [phaseIndex]: {
            deliverables: {
              $splice: [
                [fromIndex, 1],
                [toIndex, 0, prev[phaseIndex].deliverables[fromIndex]],
              ],
            }
          }
        })
      )
  };

  const handleDragEnd = () => {
    const idsInOrder = shownPhase.deliverables.map((i) => i.id);
    jQuery.post(project_phase_update_deliverable_order_path({project_id, deliverables: idsInOrder, _options: true}));
  };

  const DeliverableCard = ({deliverable, outgoingTransition, index}) => {
    const ref = useRef(null);

    const [{isOver}, drop] = useDrop({
      accept: 'BOX',
      hover: (draggedItem) => {
        if (draggedItem.index === index) return;
        moveDeliverable(draggedItem.index, index);
        draggedItem.index = index;
      },
      drop: handleDragEnd,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      })
    });

    const [{isDragging}, drag] = useDrag({
      item: {type: 'BOX', id: deliverable.id, index},
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: handleDragEnd,
    });

    drag(drop(ref));

    return (
      <div
        className={`${styles.deliverableCard} ${(!canManageProjectPhases || !isEditing) && styles.noDrag}`}
        style={{
          backgroundColor: isDragging ? "#a0e0ff" : isOver ? "#ffe0a0" : "#fafafa",
          transition: "transform 0.2s, background-color 0.2s",
        }}
        {...((canManageProjectPhases && isEditing) && {ref: ref})}
      >
        {deliverable.type === 'recurring_report' ?
          <RecurringDeliverableCardContent {...{project_id, deliverable, shownPhase, updateDeliverableSequences}}/> :
          <DeliverableCardContent {...{project_id, deliverable, outgoingTransition, updateDeliverableProgress, editable: (canManageProjectPhases && !isEditing)}}/>
        }
      </div>
    )
  };

  return (
    <div className={`${styles.deliverablesContainer} ${styles[`deliverablesContainer-${shownPhase.completed ? 'completed' : shownPhase.current ? 'active' : 'default'}`]}`}>
      <div className={styles.deliverablesGrid1}>
        <div className={styles.titleContainer}>
          {I18n.t('linked_deliverables')} ({shownPhase.deliverables.length})

          {canManageProjectPhases &&
            <button onClick={() => setIsEditing(!isEditing)} className='btn btn-link btn-sm'>
              {isEditing ?
                <><i className='fa fa-eye'/> {I18n.t('view')}</> :
                <><i className='fa fa-pencil'/> {I18n.t('edit')}</>
              }
            </button>
          }
        </div>

        <DndProvider backend={Backend}>
          <div className={styles.deliverablesGrid2}>
            {shownPhase.deliverables.map((deliverable, i) =>
              <DeliverableCard key={`deliverable-${i}`} index={i} {...{deliverable, outgoingTransition}}/>
            )}

            {(canManageProjectPhases && isEditing) &&
              <AddDeliverable {...{project_id, shownPhase, setPhases}}/>
            }
          </div>
        </DndProvider>
      </div>

      {((canManageProjectPhases && !outgoingBlocked && !isLastPhase) || outgoingTransition?.link) &&
        <div className={styles.nextArrow} onClick={handleNextStep}>
          <i className='fa-solid fa-arrow-right'/>
        </div>
      }
    </div>
  );
};

export default DeliverablesBar;
